import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Messaging } from 'firebase/messaging';
import { usePushNotificationService } from '@zonofi/common/hooks/web-push-notification-service';
import {
  addNotification,
  dispatch,
  getIsAuthenticated,
  getWorkspaceId,
  getUserAuthId,
  useAppSelector,
  useUpdateFCMTokenMutation,
} from '@zonofi/zono-money-store';
import { AppName, DevicePlatform } from '@zonofi/common/types/enum';
import { useNotificationToast } from '../notification';
import { DisplayNotificationObject } from '../types';
import { SELLER_WEBAPP_ROUTES } from '../constants';

export const useFirebasePushNotifications = (messaging: Messaging) => {
  const navigate = useNavigate();

  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const workspace = useAppSelector(getWorkspaceId);
  const [updateFCMToken] = useUpdateFCMTokenMutation();
  const userId = useAppSelector(getUserAuthId);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const { onShowToast, onCloseToast } = useNotificationToast();

  const messagingRef = useRef<Messaging | null>(null);
  const fcmTokenRef = useRef<string | null>(null);

  const getConversationNavigationPath = useCallback(
    (data?: DisplayNotificationObject) => {
      if (data?.type === 'conversation_assignment' && data.conversationId) {
        return `${SELLER_WEBAPP_ROUTES.CONVERSATION_LIST}/${data.conversationId}`;
      }
      return undefined;
    },
    []
  );

  const persistNotification = useCallback(
    ({
      title,
      body,
      data,
      source,
      id,
    }: {
      title: string;
      body: string;
      data?: DisplayNotificationObject;
      source: 'foreground' | 'background_click';
      id?: string;
    }) => {
      dispatch(
        addNotification({
          id,
          title,
          body,
          type: data?.type,
          navigationPath: getConversationNavigationPath(data),
          source,
          data: data
            ? Object.entries(data).reduce(
                (acc, [key, value]) => {
                  acc[key] = value != null ? String(value) : undefined;
                  return acc;
                },
                {} as Record<string, string | undefined>
              )
            : undefined,
        })
      );
    },
    [getConversationNavigationPath]
  );

  // Display custom notification
  const displayNotification = useCallback(
    (payload: {
      title: string;
      body: string;
      [key: string]: string | undefined;
    }) => {
      const data = payload as unknown as DisplayNotificationObject;
      const navigationPath = getConversationNavigationPath(data);

      persistNotification({
        title: data.title,
        body: data.body,
        data,
        source: 'foreground',
      });

      if (Notification.permission !== 'granted') return;

      onShowToast({
        body: data.body,
        title: data.title,
        type: 'conversation_assignment',
        navigationPath,
      });

      setTimeout(() => {
        onCloseToast();
      }, 8000);
    },
    [
      getConversationNavigationPath,
      onCloseToast,
      onShowToast,
      persistNotification,
    ]
  );

  // Handle notification click
  const handleNotificationClick = useCallback(
    (data: DisplayNotificationObject) => {
      if (data.type === 'conversation_assignment') {
        navigate(
          `${SELLER_WEBAPP_ROUTES.CONVERSATION_LIST}/${data.conversationId}`
        );
      }
    },
    [navigate]
  );

  const { addMessageListener, getClientToken } = usePushNotificationService({
    displayNotification,
  });

  const updateFCM = useCallback(
    async (fcmToken: string) => {
      if (!workspace) return;
      await updateFCMToken({
        appName: AppName.SELLER,
        token: fcmToken,
        userId: userId,
        devicePlatform: DevicePlatform.WEB,
        sellerWorkspaceId: workspace,
      });
    },
    [updateFCMToken, userId, workspace]
  );

  // Initialize Firebase Messaging safely
  const initializeFirebase = useCallback(async () => {
    if (typeof window === 'undefined') return; // Skip SSR

    try {
      if (!messaging) {
        console.warn('Firebase messaging initialization failed');
        return;
      }

      messagingRef.current = messaging;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission not granted');
        return;
      }

      const fcmToken = await getClientToken(messaging);
      if (fcmToken) {
        fcmTokenRef.current = fcmToken;
        await updateFCM(fcmToken);
      }

      addMessageListener(messaging);
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      throw error;
    }
  }, [getClientToken, addMessageListener, updateFCM]);

  // Check if Firebase is ready
  useEffect(() => {
    const checkFirebaseReady = () => {
      if (messaging) {
        setIsFirebaseReady(true);
      } else {
        setTimeout(checkFirebaseReady, 100);
      }
    };

    checkFirebaseReady();
  }, []);

  // Initialize when user is authenticated
  useEffect(() => {
    if (isAuthenticated && workspace && isFirebaseReady) {
      initializeFirebase();
    }
  }, [isAuthenticated, workspace, isFirebaseReady]);

  // Cleanup on logout
  useEffect(() => {
    if (!isAuthenticated && fcmTokenRef.current) {
      updateFCM('');
      fcmTokenRef.current = null;
    }
  }, [isAuthenticated, updateFCM]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fcmTokenRef.current) {
        const token = fcmTokenRef.current;
        fcmTokenRef.current = null;
        // Schedule async clear safely after unmount
        setTimeout(() => {
          updateFCM(token ? '' : '');
        }, 0);
      }
    };
  }, [updateFCM]);

  // Listen for messages from service worker
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        console.log(
          'Notification clicked - received data:',
          event.data.payload
        );

        const notificationData = event.data.payload;
        const parsedData = notificationData?.data as
          | DisplayNotificationObject
          | undefined;

        persistNotification({
          id: notificationData?.messageId,
          title: notificationData?.notification?.title || 'New Notification',
          body: notificationData?.notification?.body || '',
          data: parsedData,
          source: 'background_click',
        });

        if (parsedData) {
          handleNotificationClick(parsedData);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        handleServiceWorkerMessage
      );
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener(
          'message',
          handleServiceWorkerMessage
        );
      }
    };
  }, [handleNotificationClick, persistNotification]);
};
